import { computed, watch } from 'vue';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import {
  useDebounceFn,
  useEventSource as useEventSourceExternal,
  useOnline,
  useWindowFocus
} from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { ROUTE_NAMES } from '@/router/conf';
import useSnackbar from '@/composables/useSnackbar';
import { useI18n } from 'vue-i18n';
import type {
  ICountersEvent,
  IEventSourceEventData,
  IExistsEvent,
  IExpungeEvent,
  IFetchEvent
} from '@/types/services/eventSource.service';
import { EEventSourceCommand } from '@/types/services/eventSource.service.enums';
import { hashKey, type QueryKey, useQueryClient } from '@tanstack/vue-query';
import type { IWebAUthNGetCredentials } from '@/types/services/webauthn.service';
import type { IAspsGetResponse } from '@/types/services/security.service';
import type { IMcpToken } from '@/types/services/mcp.service';
import { mcpTokensQueryKey } from './useSecurityService';
import type { IAttributesResponse, IDetailsResponse } from '@/types/services/user.service';
import useChangeLocale from '@/composables/useChangeLocale';
import type { IAccountResponse, IMeResponse } from '@/types/services/auth.service';
import type { IAvatarGetResponse } from '@/types/services/avatar.service';
import { useCookies } from '@vueuse/integrations/useCookies';
import useAuthService from '@/composables/apiServices/useAuthService';
import type {
  ISettingsSignature,
  ISettingsSignatureListResponse,
  ISettingsSignatureResponse
} from '@/types/services/signature.service';
import type {
  IListMessageResponse,
  IListMessagesResponse,
  IMessageResponse
} from '@/types/services/message.service';
import type { IMassActionProgressData } from '@/types/services/massAction.service';
import type { IRecentSearchResponse } from '@/types/services/search.service';
import type { IFolderTreeResponse, IHashedFolderItem } from '@/types/services/folder.service';
import type { IContactListData } from '@/types/services/contact.service';
import { EContactOrdering } from '@/types/services/user.service.enums';
import { v4 as uuid } from 'uuid';
import {
  getMailboxAndSearchListQueriesKeyBases,
  getMailboxAndSearchListQueriesKeys,
  getMailboxAndSearchListQueriesKeysToInvalidateOnThreadingChange,
  getCollapsedThreadItemState,
  getThreadMailboxQueriesKeyBases,
  invalidateMailboxAndSearchListQueries,
  isThreadView
} from '@/helpers/mailbox';
import {
  resetStaleProgressorTimer,
  clearStaleProgressorTimer
} from '@/helpers/massActionProgressTimer';
import useUserService, { USER_SERVICE_UPDATE_DETAILS_MUTATION_KEY } from './useUserService';

const recursiveUpdateFolderListItemUnseenAndTotalCounter = (
  data: ICountersEvent,
  folderList: IHashedFolderItem[]
): IHashedFolderItem[] => {
  return folderList.map((folder) => {
    if (folder.id === data.mailbox) {
      folder.unseen = data.unseen;
      folder.total = data.total;
    } else if (folder.children) {
      folder.children = recursiveUpdateFolderListItemUnseenAndTotalCounter(data, folder.children);
    }

    return folder;
  });
};

const useEventSource = () => {
  const { userIndex, queriesEnabled } = useAuthenticatedQuery();
  const route = useRoute();
  const router = useRouter();
  const online = useOnline();
  const windowFocused = useWindowFocus();
  const { t } = useI18n();
  const { addSnackbar, removeSnackbar } = useSnackbar();
  const { languageOptions } = useChangeLocale();
  const { get: getCookie } = useCookies([]);

  const { me, removeDeviceUsers } = useAuthService();
  const { refetch } = me();
  const { mutateAsync: removeDeviceUsersMutate } = removeDeviceUsers();

  const { attributes } = useUserService();
  const { data: userAttributes } = attributes();

  const queryClient = useQueryClient();

  let reconnectSnackbarKey: string | null = null;
  let permanentSnackbarKey: string | null = null;
  let reconnectAttemptCount = 0;

  const clearReconnectSnackbar = () => {
    if (reconnectSnackbarKey) {
      removeSnackbar(reconnectSnackbarKey);
    }
    reconnectSnackbarKey = null;
  };

  const clearPermanentSnackbar = () => {
    if (permanentSnackbarKey) {
      removeSnackbar(permanentSnackbarKey);
    }
    permanentSnackbarKey = null;
  };

  const isNewWindow = computed(() =>
    route.matched.some((record) => record.name === ROUTE_NAMES.BASE_LAYOUT_TAB)
  );

  const url = computed(() =>
    !userIndex.value
      ? undefined
      : import.meta.env.VITE_UPDATES_URL
        ? new URL(`/u/${userIndex.value}`, import.meta.env.VITE_UPDATES_URL)
        : new URL(`/u/${userIndex.value}/updates`, import.meta.env.VITE_API_BASE_URL)
  );

  const onRetryFailed = () => {
    console.error('EventSource failed to reconnect');
    clearReconnectSnackbar();
    const key = addSnackbar({
      type: 'error',
      message: t('eventStream.reconnectFailed'),
      timeout: -1,
      actions: [
        {
          dataTestId: 'retry-EventSource-btn',
          actionLabel: t('common.retry'),
          callback: async () => {
            await refetch();
            open();
            clearPermanentSnackbar();
          }
        }
      ]
    });

    if (key) {
      permanentSnackbarKey = key;
    }
  };

  const { status, eventSource, error, open, close } = useEventSourceExternal(url, [], {
    withCredentials: true,
    // Turning off automatic (re)connect logic since we are handling it ourselves
    immediate: false,
    autoConnect: false,
    autoReconnect: false
  });
  let isInitialConnection = true;
  watch(status, async (status) => {
    console.debug('EventSource status changed: ', status);
    if (status === 'CLOSED') {
      console.error('EventSource error: ', error.value);
      close();

      if (!error.value) {
        return;
      }

      reconnectAttemptCount += 1;

      if (reconnectAttemptCount >= 5) {
        reconnectAttemptCount = 0;
        onRetryFailed();
        return;
      } else {
        await new Promise((resolve) => setTimeout(resolve, 2000 * reconnectAttemptCount));
        await refetch();
        open();
        return;
      }

      // if (!reconnectSnackbarKey && !permanentSnackbarKey) {
      //   const key = addSnackbar({
      //     type: 'attention',
      //     message: t('EventSource.connectionBrokenRetrying'),
      //     timeout: -1
      //     // actions: [
      //     //   {
      //     //     actionLabel: t('common.close'),
      //     //     callback: () => {
      //     //       clearReconnectSnackbar();
      //     //     }
      //     //   }
      //     // ]
      //   });

      //   if (key) {
      //     reconnectSnackbarKey = key;
      //   }
      // }
    } else if (status === 'OPEN') {
      console.debug('EventSource is opened');
      clearPermanentSnackbar();
      clearReconnectSnackbar();
      if (!isInitialConnection) {
        queryClient.invalidateQueries({
          queryKey: ['authenticatedQuery', userIndex.value],
          exact: false
        });
      }
      isInitialConnection = false;

      if (reconnectSnackbarKey) {
        // addSnackbar({
        //   type: 'success',
        //   message: t('EventSource.connectionReestablished'),
        //   timeout: 5000
        // });
      }
    }
  });

  watch(
    [url, isNewWindow, queriesEnabled, online, windowFocused],
    ([newUrl, isNewWindow, queriesEnabled, online], [oldUrl]) => {
      if (status.value === 'OPEN' && newUrl === oldUrl) {
        // If the connection is already open (and the URL hasn't changed), do nothing.
        return;
      }

      // If queries are disabled, or we are offline, close the eventsource.
      if (!queriesEnabled || !online) {
        close();
        return;
      }
      // Reconnect when the user changes.
      // Or initialize it when the user changes from "new window" view.
      if (!isNewWindow) {
        console.debug('Initializing EventSource');
        // Clear snackbars since we are changing the user
        clearPermanentSnackbar();
        clearReconnectSnackbar();
        open();
      }
    },
    { immediate: true }
  );

  const reduceLoaderCount = (
    data: IFetchEvent | IExpungeEvent | IExistsEvent
  ): { matched: boolean; completed: boolean } => {
    // matched: this event belongs to an in-flight optimistic mass action on this mailbox, so its
    // effect on the list cache is already known and a fresh list refetch would be redundant.
    // completed: the mass action has now accounted for every message it expected, so a single
    // reconciliation refetch is warranted.
    let matched = false;
    let completed = false;

    queryClient.setQueryData<IMassActionProgressData | null>(
      ['authenticatedQuery', userIndex, 'massAction', 'progress', data.mailbox],
      (oldData): IMassActionProgressData | null | undefined => {
        if (!oldData) {
          return null;
        }
        matched = true;

        if (oldData.handledUidSet.has(data.uid)) {
          return oldData;
        }

        const newData: IMassActionProgressData = {
          ...oldData,
          handledUidSet: new Set(oldData.handledUidSet)
        };
        if (
          // Flags updated
          (data.command === 'FETCH' &&
            ((oldData.action === 'flagged' && data.flags.includes('\\Flagged')) ||
              (oldData.action === 'unflag' && !data.flags.includes('\\Flagged')) ||
              (oldData.action === 'seen' && data.flags.includes('\\Seen')) ||
              (oldData.action === 'unseen' && !data.flags.includes('\\Seen')))) ||
          // Or message removed (handles all cases where the message is moved to a different folder or deleted)
          data.command === 'EXPUNGE'
        ) {
          newData.handledUidSet.add(data.uid);
        }

        if (newData.handledUidSet.size === newData.total) {
          clearStaleProgressorTimer(userIndex, data.mailbox);
          completed = true;
          return null;
        }

        // Reset the stale timer since we got an event
        resetStaleProgressorTimer(queryClient, userIndex, data.mailbox, () => {
          const currentUserIndex = userIndex.value;
          if (currentUserIndex) {
            invalidateMailboxAndSearchListQueries(queryClient, currentUserIndex, data.mailbox);
          }
        });

        return newData;
      }
    );

    return { matched, completed };
  };

  const handleEventData = async (
    data: IEventSourceEventData
  ): Promise<{
    queryKeysToBeInvalidated: QueryKey[];
    queryKeysToBeRemoved: QueryKey[];
  }> => {
    const queryKeysToBeInvalidated: QueryKey[] = [];
    const queryKeysToBeRemoved: QueryKey[] = [];
    try {
      console.debug('EventSource data received: ', data);
      if (!userIndex.value) {
        // Should never happen.
        throw new Error('User index is null');
      }
      const currentUserIndex = userIndex.value;
      switch (data.command) {
        /** Wildduck specific commands **/
        case EEventSourceCommand.COUNTERS:
          languageOptions.value.forEach(({ value: locale }) => {
            queryClient.setQueryData<IFolderTreeResponse | undefined>(
              ['authenticatedQuery', currentUserIndex, 'folders', locale],
              (oldData) => {
                if (!oldData) {
                  return undefined;
                }
                return {
                  data: recursiveUpdateFolderListItemUnseenAndTotalCounter(data, oldData.data),
                  hash: uuid()
                };
              }
            );
          });
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'folders',
            'flat'
          ]);
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'folders',
            'specialUse'
          ]);
          break;
        // Message changed: flagged, seen, unflag, unseen
        case EEventSourceCommand.FETCH:
          // Update the singular message data
          queryClient.setQueryData<IMessageResponse>(
            ['authenticatedQuery', currentUserIndex, 'mailbox', data.mailbox, 'message', data.uid],
            (oldData): IMessageResponse | undefined => {
              if (!oldData) {
                return undefined;
              }

              return {
                ...oldData,
                seen: data.flags.includes('\\Seen'),
                // draft: data.flags.includes('\\Draft'),
                flagged: data.flags.includes('\\Flagged'),
                // For some reason both flags are present when answering
                answered: data.flags.includes('\\Answered') && !data.flags.includes('$Forwarded'),
                forwarded: data.flags.includes('$Forwarded'),
                date: data.hdate ?? oldData.date
              };
            }
          );

          // Inline update all of the list requests that are related
          // Mailbox list, search list, thread list
          if (currentUserIndex !== null) {
            const { matched: fetchEventMatched, completed: fetchEventCompleted } =
              reduceLoaderCount(data);
            const eventSeen = data.flags.includes('\\Seen');
            const eventFlagged = data.flags.includes('\\Flagged');
            const queryKeysToBeUpdatedByFetch = new Map<readonly unknown[], true>();
            queryClient.setQueryData<IListMessagesResponse>(
              ['authenticatedQuery', currentUserIndex, 'thread', data.thread],
              (oldData): IListMessagesResponse | undefined => {
                if (!oldData) {
                  return undefined;
                }

                return {
                  ...oldData,
                  results: oldData.results.map((message) => {
                    // If threading is enabled we need to update all related mailbox and search list queries
                    if (userAttributes.value?.threadingShownState) {
                      getMailboxAndSearchListQueriesKeys(
                        queryClient,
                        currentUserIndex,
                        message.mailbox
                      ).forEach((queryKey) => {
                        queryKeysToBeUpdatedByFetch.set(queryKey, true);
                      });
                    }
                    if (message.id === data.uid && message.mailbox === data.mailbox) {
                      // Always update the event related mailbox and search list queries
                      getMailboxAndSearchListQueriesKeys(
                        queryClient,
                        currentUserIndex,
                        message.mailbox
                      ).forEach((queryKey) => {
                        queryKeysToBeUpdatedByFetch.set(queryKey, true);
                      });
                      return {
                        ...message,
                        seen: eventSeen,
                        flagged: eventFlagged,
                        // For some reason both flags are present when answering
                        answered:
                          data.flags.includes('\\Answered') && !data.flags.includes('$Forwarded'),
                        forwarded: data.flags.includes('$Forwarded'),
                        date: data.hdate ?? message.date
                      };
                    }
                    return message;
                  })
                };
              }
            );
            // In threading view a list item represents the whole thread, so its state is the
            // thread's aggregate (all seen / any flagged), not the single message's flags from
            // the event.
            const updatedThread = queryClient.getQueryData<IListMessagesResponse>([
              'authenticatedQuery',
              currentUserIndex,
              'thread',
              data.thread
            ]);
            const changedMessageIsCached =
              !!updatedThread &&
              updatedThread.results.some(
                (message) => message.id === data.uid && message.mailbox === data.mailbox
              );
            const threadState = getCollapsedThreadItemState(
              changedMessageIsCached ? updatedThread.results : undefined,
              // The event's message guarantees these directions even when the full thread is unknown.
              !eventSeen,
              eventFlagged
            );

            // Update the seen and flagged states of the matching items in all of the mailbox and
            // search listings that contain the thread. Mailbox listings display collapsed
            // threads, so the thread's aggregate state applies to them; search results are
            // per-message, so only the exact message is updated there.
            for (const queryKey of queryKeysToBeUpdatedByFetch.keys()) {
              const isMailboxListKey = queryKey[2] === 'mailbox';
              queryClient.setQueryData<IListMessagesResponse>(
                queryKey,
                (oldData): IListMessagesResponse | undefined => {
                  if (!oldData) {
                    return undefined;
                  }

                  return {
                    ...oldData,
                    results: oldData.results.map((message) => {
                      if (
                        isMailboxListKey &&
                        userAttributes.value?.threadingShownState &&
                        message.thread === data.thread
                      ) {
                        const updated: IListMessageResponse = { ...message };
                        if (threadState.seen !== null) {
                          updated.seen = threadState.seen;
                        }
                        if (threadState.flagged !== null) {
                          updated.flagged = threadState.flagged;
                        }
                        return updated;
                      }
                      if (message.id === data.uid && message.mailbox === data.mailbox) {
                        return {
                          ...message,
                          seen: eventSeen,
                          flagged: eventFlagged,
                          // For some reason both flags are present when answering
                          answered:
                            data.flags.includes('\\Answered') && !data.flags.includes('$Forwarded'),
                          forwarded: data.flags.includes('$Forwarded'),
                          date: data.hdate ?? message.date
                        };
                      }
                      return message;
                    })
                  };
                }
              );
            }

            // When a state could not be determined locally (the full thread is unknown and the
            // event doesn't guarantee its direction), let the server recompute the aggregate.
            // Skipped for events belonging to an in-flight optimistic mass action; its single
            // completion reconciliation refetch covers them.
            if (
              userAttributes.value?.threadingShownState &&
              (threadState.seen === null || threadState.flagged === null) &&
              (!fetchEventMatched || fetchEventCompleted)
            ) {
              queryKeysToBeInvalidated.push(
                ...getMailboxAndSearchListQueriesKeyBases(currentUserIndex, data.mailbox),
                ['authenticatedQuery', currentUserIndex, 'thread', data.thread]
              );
            }
          }
          break;
        // Message added to mailbox: spam, trash, moveTo, new email
        case EEventSourceCommand.EXISTS: {
          const { matched, completed } = reduceLoaderCount(data);
          // Skip the list refetch when this arrival is already accounted for by an in-flight
          // optimistic mass action on this mailbox; a single refetch happens once it completes.
          if (!matched || completed) {
            queryKeysToBeInvalidated.push(
              ...getMailboxAndSearchListQueriesKeyBases(currentUserIndex, data.mailbox),
              ...getThreadMailboxQueriesKeyBases(queryClient, currentUserIndex, data.thread)
            );
          }

          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'thread',
            data.thread
          ]);
          break;
        }
        // Message removed from mailbox: spam, trash, moveTo, delete
        case EEventSourceCommand.EXPUNGE: {
          const { matched, completed } = reduceLoaderCount(data);
          // Skip the list refetch when this removal is already accounted for by an in-flight
          // optimistic mass action on this mailbox; a single refetch happens once it completes.
          if (!matched || completed) {
            queryKeysToBeInvalidated.push(
              ...getMailboxAndSearchListQueriesKeyBases(currentUserIndex, data.mailbox),
              ...getThreadMailboxQueriesKeyBases(queryClient, currentUserIndex, data.thread)
            );
          }
          // Clear the thread request cache
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'thread',
            data.thread
          ]);
          // Drop the message from the cache without invalidating it: the message no longer
          // exists in this mailbox, so invalidating an active detail query would refetch it
          // and 404 (e.g. cancelling a scheduled send, when the expunge event can be processed
          // before the redirect to the new draft has unmounted the message view)
          queryKeysToBeRemoved.push([
            'authenticatedQuery',
            currentUserIndex,
            'mailbox',
            data.mailbox,
            'message',
            data.uid
          ]);
          // Keep the draft query invalidated (not removed): the composer relies on the refetch
          // 404ing to detect that an open draft was deleted and reset the composition
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'draft',
            'get',
            data.uid
          ]);

          break;
        }

        /** Webmail specific commands **/
        case EEventSourceCommand.WEB_AUTHN_AUTHENTICATOR_REMOVED:
          if (data.data.type === 'WebAuthN') {
            queryClient.setQueryData<IWebAUthNGetCredentials>(
              ['authenticatedQuery', currentUserIndex, 'webauthn', 'credentials'],
              (oldCredentials): IWebAUthNGetCredentials | undefined => {
                if (!oldCredentials) {
                  return undefined;
                }
                return {
                  ...oldCredentials,
                  credentials: oldCredentials.credentials.filter(
                    (e) => e.id !== data.data.authenticatorId
                  )
                };
              }
            );
          }
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'settings',
            'security',
            'get2fa'
          ]);
          break;
        case EEventSourceCommand.WEB_AUTHN_AUTHENTICATOR_ADDED:
          if (data.data.type === 'WebAuthN') {
            queryKeysToBeInvalidated.push([
              'authenticatedQuery',
              currentUserIndex,
              'webauthn',
              'credentials'
            ]);
          }
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'settings',
            'security',
            'get2fa'
          ]);
          break;
        case EEventSourceCommand.APPLICATION_PASSWORD_ADDED:
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'settings',
            'security',
            'getAsps'
          ]);
          break;
        case EEventSourceCommand.APPLICATION_PASSWORD_REMOVED:
          queryClient.setQueryData<IAspsGetResponse[]>(
            ['authenticatedQuery', currentUserIndex, 'settings', 'security', 'getAsps'],
            (asps): undefined | IAspsGetResponse[] => {
              if (!asps) {
                return undefined;
              }
              return asps.filter((password) => password.id !== data.data.id);
            }
          );
          break;
        case EEventSourceCommand.MCP_TOKEN_CREATED:
          queryKeysToBeInvalidated.push(mcpTokensQueryKey(currentUserIndex));
          break;
        case EEventSourceCommand.MCP_TOKENS_DELETED:
          queryClient.setQueryData<IMcpToken[]>(
            mcpTokensQueryKey(currentUserIndex),
            (tokens): undefined | IMcpToken[] => {
              if (!tokens) {
                return undefined;
              }
              return tokens.filter((token) => !data.data.ids.includes(token.id));
            }
          );
          break;
        case EEventSourceCommand.DETAILS_UPDATED:
          if (!queryClient.isMutating({ mutationKey: USER_SERVICE_UPDATE_DETAILS_MUTATION_KEY })) {
            queryClient.setQueryData<IDetailsResponse>(
              ['authenticatedQuery', currentUserIndex, 'user', 'details'],
              (oldDetails): IDetailsResponse | undefined => {
                if (!oldDetails) {
                  return undefined;
                }

                return {
                  id: oldDetails.id,
                  wdId: oldDetails.wdId,
                  name: data.data.name ?? oldDetails.name,
                  address: oldDetails.address,
                  targets: data.data.targets ?? oldDetails.targets,
                  spamLevel: data.data.spamLevel ?? oldDetails.spamLevel,
                  meta: {
                    ...oldDetails.meta,
                    onBoardingCompleted:
                      data.data.onboarding_completed ?? oldDetails.meta.onBoardingCompleted,
                    browserTimezoneSet:
                      data.data.browser_timezone_set ?? oldDetails.meta.browserTimezoneSet,
                    displayDensity:
                      data.data.metaData?.displayDensity ?? oldDetails.meta.displayDensity,
                    inboxType: data.data.metaData?.inboxType ?? oldDetails.meta.inboxType,
                    showContactList:
                      data.data.metaData?.showContactList ?? oldDetails.meta.showContactList,
                    ui: data.data.metaData?.ui
                      ? {
                          color: data.data.metaData.ui.color,
                          theme: data.data.metaData.ui.theme,
                          useBrowserDark: false
                        }
                      : oldDetails.meta.ui,
                    upgrade_request:
                      data.data.metaData?.upgrade_request === null
                        ? null
                        : data.data.metaData?.upgrade_request !== undefined
                          ? data.data.metaData.upgrade_request
                          : oldDetails.meta.upgrade_request,
                    forward: data.data.metaData?.forward ?? oldDetails.meta.forward
                  },
                  timezone: data.data.metaData?.timezone ?? oldDetails.timezone,
                  view_mode: data.data.metaData?.viewMode ?? oldDetails.view_mode,
                  language: data.data.metaData?.language ?? oldDetails.language
                };
              }
            );
          }
          queryClient.setQueryData<IAccountResponse[]>(
            ['getAccounts'],
            (oldDetails): IAccountResponse[] | undefined => {
              if (!oldDetails) {
                return undefined;
              }

              return oldDetails.map((account) => {
                if (account.uid.toString() === currentUserIndex) {
                  return { ...account, name: data.data.name ?? account.name };
                }
                return account;
              });
            }
          );
          queryClient.setQueryData<IMeResponse>(
            ['authenticatedQuery', currentUserIndex, 'me'],
            (oldDetails): IMeResponse | undefined => {
              if (!oldDetails) {
                return undefined;
              }

              return { ...oldDetails, name: data.data.name ?? oldDetails.name };
            }
          );

          break;
        case EEventSourceCommand.ATTRIBUTES_UPDATED:
          // Collect the keys before the cache is updated below: the decision falls back to
          // the currently cached attributes for changes initiated in other tabs.
          queryKeysToBeInvalidated.push(
            ...getMailboxAndSearchListQueriesKeysToInvalidateOnThreadingChange(
              queryClient,
              currentUserIndex,
              data.data
            )
          );
          queryClient.setQueryData<IAttributesResponse>(
            ['authenticatedQuery', currentUserIndex, 'user', 'attributes'],
            (oldAttributes): IAttributesResponse | undefined => {
              if (!oldAttributes) {
                return undefined;
              }

              return { ...oldAttributes, ...data.data };
            }
          );
          break;
        case EEventSourceCommand.AVATAR_UPLOADED:
          queryKeysToBeInvalidated.push(['getAccounts']);
          queryKeysToBeInvalidated.push(['authenticatedQuery', currentUserIndex, 'getAvatar']);
          break;
        case EEventSourceCommand.AVATAR_REMOVED:
          queryClient.setQueryData<IAccountResponse[]>(
            ['getAccounts'],
            (oldData): IAccountResponse[] | undefined => {
              if (!oldData) {
                return undefined;
              }
              return oldData.map((account) => {
                if (account.uid === Number(currentUserIndex)) {
                  return { ...account, avatar: undefined } satisfies IAccountResponse;
                }
                return account;
              });
            }
          );
          queryClient.setQueryData<IAvatarGetResponse | null>(
            ['authenticatedQuery', currentUserIndex, 'getAvatar'],
            () => {
              return null;
            }
          );
          break;
        case EEventSourceCommand.LOG_OUT_DEVICE_USER:
          if (getCookie('duuid') === data.data.deviceId) {
            const users = queryClient.getQueryData<IAccountResponse[]>(['getAccounts']);
            // If there were more than one user logged in before, redirect to user select else to
            // password view
            if ((users?.length ?? 0) > 1) {
              await router.push({
                name: ROUTE_NAMES.AUTH_LOGIN_USER_SELECT,
                query: { reason: 'streamDeviceUserLogout' }
              });
            } else {
              await router.push({
                name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD,
                query: { reason: 'streamDeviceUserLogout' }
              });
            }
            queryClient.removeQueries({ queryKey: ['authenticatedQuery', currentUserIndex] });
            queryClient.removeQueries({ queryKey: ['getAccounts'] });
          }
          break;
        case EEventSourceCommand.LOG_OUT_DEVICE:
          if (getCookie('duuid') === data.data.deviceId || !getCookie('duuid')) {
            console.debug(`Removing device ${data.data.deviceId} users`);
            await router.push({
              name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD,
              query: { reason: 'streamDeviceLogout' }
            });
            await removeDeviceUsersMutate();
            queryClient.removeQueries({ queryKey: ['authenticatedQuery'] });
            queryClient.removeQueries({ queryKey: ['getAccounts'] });
          }
          break;
        case EEventSourceCommand.LOG_OUT_USER:
          await router.push({
            name: ROUTE_NAMES.AUTH_LOGIN_USER_SELECT,
            query: { reason: 'streamDeviceUserLogout' }
          });
          queryClient.removeQueries({ queryKey: ['authenticatedQuery', currentUserIndex] });
          queryClient.removeQueries({ queryKey: ['getAccounts'] });
          break;
        case EEventSourceCommand.ADDRESSBOOK_UPDATED:
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'addressbooks',
            'list'
          ]);
          break;
        case EEventSourceCommand.ADDRESSBOOK_DELETED:
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'addressbooks',
            'list'
          ]);
          queryKeysToBeInvalidated.push(['authenticatedQuery', currentUserIndex, 'groups', 'list']);
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'contacts',
            'list'
          ]);
          break;
        case EEventSourceCommand.CONTACT_UPDATED:
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'addressbooks',
            'list'
          ]);
          queryKeysToBeInvalidated.push(['authenticatedQuery', currentUserIndex, 'groups', 'list']);
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'contacts',
            'list'
          ]);
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'contacts',
            data.data.contact.data.id,
            'avatar'
          ]);
          break;
        case EEventSourceCommand.CONTACTS_IMPORTED:
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'addressbooks',
            'list'
          ]);
          queryKeysToBeInvalidated.push(['authenticatedQuery', currentUserIndex, 'groups', 'list']);
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'contacts',
            'list'
          ]);
          break;
        case EEventSourceCommand.CONTACT_DELETED:
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'addressbooks',
            'list'
          ]);
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'contacts',
            'list'
          ]);
          queryClient.removeQueries({
            queryKey: [
              'authenticatedQuery',
              currentUserIndex,
              'contacts',
              data.data.contactId,
              'avatar'
            ]
          });
          break;
        case EEventSourceCommand.CONTACT_USED_AT_UPDATED: {
          const updatedIds = new Set(data.data.contactIds);
          queryClient.setQueryData<IContactListData>(
            ['authenticatedQuery', currentUserIndex, 'contacts', 'list', EContactOrdering.USED_AT],
            (oldData) => {
              if (!oldData) {
                return oldData;
              }
              const matched = oldData.filter((contact) => updatedIds.has(contact.id));
              if (matched.length === 0) {
                return oldData;
              }
              const rest = oldData.filter((contact) => !updatedIds.has(contact.id));
              return [...matched, ...rest];
            }
          );
          break;
        }
        case EEventSourceCommand.GROUP_UPDATED:
          queryKeysToBeInvalidated.push(['authenticatedQuery', currentUserIndex, 'groups', 'list']);
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'contacts',
            'list'
          ]);
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'contacts',
            'group',
            data.data.group.data.id,
            'avatar'
          ]);
          break;
        case EEventSourceCommand.GROUP_DELETED:
          queryKeysToBeInvalidated.push(['authenticatedQuery', currentUserIndex, 'groups', 'list']);
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'contacts',
            'list'
          ]);
          queryClient.removeQueries({
            queryKey: [
              'authenticatedQuery',
              currentUserIndex,
              'contacts',
              'group',
              data.data.groupId,
              'avatar'
            ]
          });
          break;
        case EEventSourceCommand.SIGNATURE_UPDATED:
          queryClient.setQueryData<ISettingsSignatureListResponse>(
            ['authenticatedQuery', currentUserIndex, 'settings', 'signatures'],
            (oldData): ISettingsSignatureListResponse | undefined => {
              if (!oldData) {
                return undefined;
              }

              const newData: ISettingsSignatureListResponse = { data: [] };
              let hasUpdated = false;
              for (const oldObject of oldData.data) {
                if (oldObject.id === data.data.signature.data.id) {
                  newData.data.push(data.data.signature.data);
                  hasUpdated = true;
                } else if (data.data.signature.data.default) {
                  const oldCopy: ISettingsSignature = { ...oldObject, default: false };
                  newData.data.push(oldCopy);
                } else {
                  newData.data.push(oldObject);
                }
              }

              if (!hasUpdated) {
                newData.data.push(data.data.signature.data);
              }

              return newData;
            }
          );
          queryClient.setQueryData<ISettingsSignatureResponse>(
            [
              'authenticatedQuery',
              currentUserIndex,
              'settings',
              'signatures',
              data.data.signature.data.id
            ],
            (): ISettingsSignatureResponse | undefined => {
              return data.data.signature;
            }
          );
          break;
        case EEventSourceCommand.SIGNATURE_DELETED:
          queryClient.removeQueries({
            queryKey: [
              'authenticatedQuery',
              currentUserIndex,
              'settings',
              'signatures',
              data.data.signatureId
            ]
          });
          queryClient.setQueryData<ISettingsSignatureListResponse>(
            ['authenticatedQuery', currentUserIndex, 'settings', 'signatures'],
            (oldData): ISettingsSignatureListResponse | undefined => {
              if (!oldData) {
                return undefined;
              }

              return {
                ...oldData.data,
                data: oldData.data.filter((signature) => signature.id !== data.data.signatureId)
              };
            }
          );

          break;
        case EEventSourceCommand.FOLDER_CREATE:
        case EEventSourceCommand.FOLDER_DELETE:
        case EEventSourceCommand.FOLDER_ORDER:
        case EEventSourceCommand.FOLDERS_ORDER_UPDATED:
        case EEventSourceCommand.FOLDER_RENAMED:
        case EEventSourceCommand.FOLDER_UPDATED:
          languageOptions.value.forEach(({ value: locale }) => {
            queryKeysToBeInvalidated.push([
              'authenticatedQuery',
              currentUserIndex,
              'folders',
              locale
            ]);
            queryKeysToBeInvalidated.push([
              'authenticatedQuery',
              currentUserIndex,
              'folders',
              'flat',
              locale
            ]);
            queryKeysToBeInvalidated.push([
              'authenticatedQuery',
              currentUserIndex,
              'folders',
              'specialUse',
              locale
            ]);
          });
          break;
        case EEventSourceCommand.WHITELIST_EMAIL_UPDATED:
        case EEventSourceCommand.WHITELIST_EMAIL_DELETED:
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'settings',
            'whitelist',
            'list'
          ]);
          queryKeysToBeInvalidated.push([
            'authenticatedQuery',
            currentUserIndex,
            'settings',
            'whitelist',
            'verified'
          ]);
          break;
        case EEventSourceCommand.FILTER_UPDATED:
        case EEventSourceCommand.FILTER_MOVE_TO_BOTTOM:
        case EEventSourceCommand.FILTER_DELETED:
        case EEventSourceCommand.FILTER_CREATED:
        case EEventSourceCommand.FILTER_IMPORTED:
          queryKeysToBeInvalidated.push(['authenticatedQuery', currentUserIndex, 'filters']);
          break;
        case EEventSourceCommand.FILTER_IMPORT_FAILED:
          addSnackbar({
            type: 'attention',
            message: t('settings.filters.importDialog.failedPartially'),
            timeout: 10_000
          });
          break;
        case EEventSourceCommand.FILTER_IMPORT_SUCCESS:
          addSnackbar({
            type: 'success',
            message: t('settings.filters.importDialog.success'),
            timeout: 10_000
          });
          break;
        case EEventSourceCommand.FILTER_IMPORT_SKIPPED:
          addSnackbar({
            type: 'attention',
            message: t('settings.filters.importDialog.skipped'),
            timeout: 10_000
          });
          break;
        case EEventSourceCommand.RECENT_SEARCH_UPDATED:
          queryClient.setQueryData<IRecentSearchResponse[]>(
            ['authenticatedQuery', userIndex, 'search', 'recentSearches'],
            (oldData) => {
              if (!oldData) {
                return oldData;
              }
              const existingSearch = oldData.find(
                (search) => search.id === data.data.recentSearch.id
              );
              if (existingSearch) {
                return oldData.map((search) =>
                  search.id === data.data.recentSearch.id ? data.data.recentSearch : search
                );
              }
              if (oldData.length < 5) {
                return [...oldData, data.data.recentSearch];
              }
              return [...oldData.slice(1), data.data.recentSearch];
            }
          );
          break;
        case EEventSourceCommand.RECENT_SEARCH_DELETED:
          queryClient.setQueryData<IRecentSearchResponse[]>(
            ['authenticatedQuery', userIndex, 'search', 'recentSearches'],
            (oldData) => {
              if (!oldData) {
                return oldData;
              }
              return oldData.filter((search) => search.id !== parseInt(data.data.id));
            }
          );
          break;
        case EEventSourceCommand.REMOTE_CONTENT_WHITELIST:
          if (isThreadView(route) && route.params.threadId) {
            queryKeysToBeInvalidated.push([
              'authenticatedQuery',
              currentUserIndex,
              'thread',
              route.params.threadId
            ]);
            queryKeysToBeInvalidated.push([
              'authenticatedQuery',
              currentUserIndex,
              'mailbox',
              route.params.mailboxId,
              'message',
              parseInt(route.params.messageId?.toString() ?? '-1', 10)
            ]);
          }

          break;
        case EEventSourceCommand.UPGRADE_REQUEST_RESET:
          queryClient.setQueryData<IDetailsResponse>(
            ['authenticatedQuery', currentUserIndex, 'user', 'details'],
            (oldData) => {
              if (!oldData) {
                return oldData;
              }
              return {
                ...oldData,
                meta: {
                  ...oldData.meta,
                  upgrade_request: null
                }
              };
            }
          );

          break;
        default:
          throw new Error(`Unknown EventSourceMessage: ${data}`);
      }
      console.debug(`Returning ${queryKeysToBeInvalidated.length} query keys to invalidate`);
    } catch (err) {
      console.warn(err);
      // MAYBE TODO: Notify user that something is broken
      if (err instanceof SyntaxError) {
        console.error('Invalid JSON payload received.', err.message);
      }
    }
    return { queryKeysToBeInvalidated, queryKeysToBeRemoved };
  };

  // Put all the actions coming in from EventSource into a queue, and then process them in a debounced manner.
  const queue: IEventSourceEventData[] = [];
  const debouncedHandleEventData = useDebounceFn(
    async () => {
      if (queue.length === 0) {
        return;
      }

      // async function sleep(msec) {
      //   return new Promise((resolve) => setTimeout(resolve, msec));
      // }

      const allQueryKeysToBeInvalidated: QueryKey[] = [];
      const allQueryKeysToBeRemoved: QueryKey[] = [];
      const eventRemovalKeys: QueryKey[] = [];
      do {
        const data = queue.shift();
        if (data) {
          const { queryKeysToBeInvalidated, queryKeysToBeRemoved } = await handleEventData(data);
          // await sleep(200);
          allQueryKeysToBeInvalidated.push(...queryKeysToBeInvalidated);
          eventRemovalKeys.push(...queryKeysToBeRemoved);
        }
      } while (queue.length > 0);

      if (import.meta.env.MODE === 'development') {
        console.debug(`Reducing ${allQueryKeysToBeInvalidated.length} query keys to invalidate`);
      }

      // Remove the queries the events asked to drop unconditionally (e.g. expunged message
      // detail queries) before invalidating anything, so a removed query can never be
      // refetched by this batch
      const eventRemovalKeysObject = eventRemovalKeys.reduce<Record<string, void>>((res, curr) => {
        const stringKey = hashKey(curr);
        if (stringKey in res) {
          return res;
        }
        res[stringKey] = queryClient.removeQueries({ queryKey: curr });
        if (import.meta.env.MODE === 'development') {
          console.debug('Removed query', curr);
        }
        return res;
      }, {});
      const eventRemovalKeysValues = Object.values(eventRemovalKeysObject);
      console.debug(`Removed ${eventRemovalKeysValues.length} queries`);

      const invalidationPromisesObject = allQueryKeysToBeInvalidated.reduce<
        Record<string, Promise<void> | void>
      >((res, curr) => {
        const stringKey = hashKey(curr);
        if (stringKey in res) {
          return res;
        }
        const inactiveQueries = queryClient.getQueriesData({
          queryKey: curr,
          exact: false,
          type: 'inactive'
        });
        const activeQueries = queryClient.getQueriesData({
          queryKey: curr,
          exact: false,
          type: 'active'
        });
        activeQueries.forEach(([key]) => {
          res[hashKey(key)] = queryClient.invalidateQueries({
            queryKey: key,
            exact: false
          });
          if (import.meta.env.MODE === 'development') {
            console.debug('Invalidating query', curr);
          }
        });
        inactiveQueries.forEach(([key]) => {
          allQueryKeysToBeRemoved.push(key);
        });
        res[stringKey] = void 0;
        return res;
      }, {});
      const invalidationPromises = Object.values(invalidationPromisesObject);
      console.debug(`Invalidating ${invalidationPromises.length} queries`);
      const removedPromisesObject = allQueryKeysToBeRemoved.reduce<Record<string, void>>(
        (res, curr) => {
          const stringKey = hashKey(curr);
          if (stringKey in res) {
            return res;
          }
          res[stringKey] = queryClient.removeQueries({ queryKey: curr });
          if (import.meta.env.MODE === 'development') {
            console.debug('Removed query', curr);
          }
          return res;
        },
        {}
      );
      const removedPromises = Object.values(removedPromisesObject);
      console.debug(`Removed ${removedPromises.length} queries`);
      await Promise.all(invalidationPromises);
    },
    250,
    { maxWait: 2500 }
  );

  let abortController: AbortController = new AbortController();

  watch(
    eventSource,
    () => {
      if (!eventSource.value) {
        abortController.abort();
        return;
      }
      abortController = new AbortController();
      eventSource.value.addEventListener(
        'message',
        (event: MessageEvent<string>) => {
          const data = JSON.parse(event.data) as IEventSourceEventData;
          queue.push(data);
          debouncedHandleEventData();
        },
        { signal: abortController.signal }
      );
    },
    { immediate: true }
  );

  return { close };
};

export default useEventSource;
